/**
 * Centralised XSS sanitisation.
 *
 * Default DOMPurify is permissive (allows many formatting tags). For
 * user-generated content we should pick the smallest allowlist that still
 * renders what the surface needs.
 *
 * - `stripHtml`: removes ALL HTML, returns plain text. Use when the surface
 *   doesn't need formatting (search history, search highlights, alt text, etc.)
 *   This is preferable to sanitize() with `KEEP_CONTENT: true` because it
 *   guarantees the result has zero element nodes.
 *
 * - `sanitizeReview`: review comments. Allows minimal formatting tags only.
 *   No <a>, no <img>, no event handlers, no style attributes.
 *
 * Always sanitise on the read path. The backend may also sanitise on write,
 * but UI-side sanitisation defends against a future API regression too.
 */
import DOMPurify from 'dompurify';

/** Strip every HTML tag — returns text content only. */
export function stripHtml(input: string): string {
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  });
}

/**
 * Review-comment allowlist — minimal formatting, no links/images/events.
 * Linkifying review content is a deliberate non-feature; reviewers shouldn't
 * be able to push outbound links.
 */
export function sanitizeReview(input: string): string {
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
    FORBID_TAGS: ['style', 'script', 'iframe', 'a', 'img', 'svg'],
    FORBID_ATTR: ['style', 'class', 'id'],
    USE_PROFILES: { html: true },
  });
}
